import React, { Component } from 'react';
import ContextButton from 'components/ContextButton';
import SelectableList from 'components/SelectableList';
import TriggerPopover from 'components/connectedForm/TriggerPopover';
import DeprecatedTooltip from 'components/connectedForm/Tooltip';
import styles from 'styles/partials/_navbar.css';
import { Link } from 'react-router-dom';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import NavBarAddDialog from '../NavBar/NavBarAddDialog';
import hasGravatar from 'utils/hasGravatar';
import md5 from 'md5';
import OmniSearchBar from '../SearchBar/OmniSearchBar';
import AboutModal from '../modals/AboutModal';
import ActionCentrePanel from './ActionCentrePanel';
import useMenuOptions from '../MenuOptions/useMenuOptions';
import { Badge, Tooltip } from '@fluentui/react-components';
import { CustomResourceCenterLauncher } from '../Userflow/CustomResourceCenterLauncher';
import FluentModal from '../modals/FluentModal';
import { SubmitRequestForm } from '../ResourceCenter/SubmitRequestForm';
import { ProductFeedbackForm } from '../ResourceCenter/ProductFeedbackForm';
import Cookies from 'universal-cookie';
import { CompanySelector } from './CompanySelector';

export const OPT_OUT_GUIDED_TOURS_COOKIE = 'optOutGuidedTours';

const BUTTON_PADDING = {
  marginLeft: '4px',
  marginRight: '10px',
};

class NavBar extends Component {
  constructor(props) {
    super(props);
    this.focusAppArea = this.focusAppArea.bind(this);
    this.handleSelectUserContextMenu = this.handleSelectUserContextMenu.bind(this);
    this.getUserIcon = this.getUserIcon.bind(this);
    this.setGravatar = this.setGravatar.bind(this);
    this.search = this.search.bind(this);
    this.toggleNotificationsPanel = this.toggleNotificationsPanel.bind(this);
    this.toggleAboutModal = this.toggleAboutModal.bind(this);

    this.toggleSubmitRequestModal = this.toggleSubmitRequestModal.bind(this);
    this.toggleProductFeedbackModal = this.toggleProductFeedbackModal.bind(this);
    this.toggleOptOutOfGuidedTours = this.toggleOptOutOfGuidedTours.bind(this);
    this.cookies = new Cookies();

    this.userContextRef = React.createRef();

    this.state = {
      navMenuSearch: [],
      openAddDialog: false,
      openAboutDialog: false,
      openSubmitRequestDialog: false,
      openProductFeedbackDialog: false,
    };
  }

  componentDidMount() {
    if (this.props.user) hasGravatar(this.props.user.email, this.setGravatar);
  }

  setGravatar(status) {
    this.gravatarUrl = status === 200 ? `https://www.gravatar.com/avatar/${md5(this.props.user.email)}?s=24` : null;
  }

  toggleSubmitRequestModal() {
    this.setState({
      openSubmitRequestDialog: true,
    });
  }

  toggleProductFeedbackModal() {
    this.setState({
      openProductFeedbackDialog: true,
    });
  }

  toggleOptOutOfGuidedTours() {
    const optOutGuidedTours = this.cookies.get(OPT_OUT_GUIDED_TOURS_COOKIE) === 'true';
    this.cookies.set(OPT_OUT_GUIDED_TOURS_COOKIE, !optOutGuidedTours);
  }

  toggleAboutModal() {
    this.setState({
      openAboutDialog: true,
    });
  }

  toggleNotificationsPanel() {
    this.setState({ notificationPanelOpen: true });
  }

  focusAppArea() {
    this.props.focusArea('App', false);
  }

  handleSelectUserContextMenu(option) {
    this.userContextRef.current.popoutClose();
    if (option.url) {
      this.props.history.push({
        pathname: option.url,
        state: { previousLocation: this.props.history.location?.pathname || 'back' },
      });
      return;
    }
    if (option.call) {
      option.call();
    }
  }

  getUserIcon() {
    return (
      <i style={{ fontSize: '24px', color: '#e1dff1' }} className="icon">
        perm_identity
      </i>
    );
  }

  search(searchValue) {
    const searchMenuOptions = this.props.menuOptions.searchOptions(searchValue);
    searchMenuOptions.sort((a, b) => {
      const keyA = a.count;
      const keyB = b.count;
      if (keyA > keyB) return -1;
      if (keyA < keyB) return 1;
      return 0;
    });
    return Promise.resolve({ options: searchMenuOptions, complete: true });
  }

  render() {
    const accountNavOptions = [
      {
        description: 'System User',
        url: `/System/SystemUser/Details/${this.props.user.id}`,
      },
      { description: 'Release Info', call: this.toggleAboutModal },
      { description: 'Log Out', call: this.props.onLogoutClick },
    ];

    const submitRequestModal = (
      <FluentModal isOpen={this.state.openSubmitRequestDialog}>
        <SubmitRequestForm onClose={() => this.setState({ openSubmitRequestDialog: false })} />
      </FluentModal>
    );

    const productFeedbackModal = (
      <FluentModal isOpen={this.state.openProductFeedbackDialog}>
        <ProductFeedbackForm onClose={() => this.setState({ openProductFeedbackDialog: false })} />
      </FluentModal>
    );

    const aboutModal = (
      <AboutModal isOpen={this.state.openAboutDialog} requestClose={() => this.setState({ openAboutDialog: false })} />
    );

    return (
      <nav className={styles.appHeader} onClick={this.focusAppArea} ref={this.props.forwardedRef}>
        <Link to="/" className={styles.homeButton} href="/#" tabIndex={-1}>
          <TriggerPopover
            flexBox={false}
            popover={<DeprecatedTooltip position="wideappicon" value="Back to Profit4 dashboard" />}
          >
            <img height={34} src="/images/OGL-Profit4ICONlogo-WHT.png" />
          </TriggerPopover>
        </Link>

        <CompanySelector tabIndex={this.props.tabIndex} onCompanyClick={this.props.onCompanyClick} />
        {this.props.company?.testMode ? (
          <div className={styles.testModeContainer}>
            <div className={styles.testMode}>
              <Badge shape="rounded" color="danger">
                Test Mode
              </Badge>
            </div>
          </div>
        ) : null}
        <OmniSearchBar />
        <div className={styles.rightNav}>
          <div className={styles.userName}>{this.props.user.displayName}</div>
          <Tooltip content={'Profit4 Resource Center'} relationship={'label'}>
            <div className={styles.addNew} style={BUTTON_PADDING}>
              <CustomResourceCenterLauncher featureFlags={this.props.featureFlags} systemUser={this.props.user} />
            </div>
          </Tooltip>
          <Tooltip content={'View recent notifications'} relationship={'label'}>
            <div
              className={styles.addNew}
              onClick={this.toggleNotificationsPanel}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  this.toggleNotificationsPanel();
                }
              }}
              role="button"
              tabIndex={0}
              style={BUTTON_PADDING}
            >
              <div style={{ display: 'inline-block' }}>
                <i style={{ fontSize: '24px' }} className="icon">
                  notifications_paused
                </i>
              </div>
            </div>
          </Tooltip>
          {this.props.company ? (
            <Tooltip content={'Add new records'} relationship={'label'}>
              <NavBarAddDialog />
            </Tooltip>
          ) : null}

          {/* Do not remove these. They're used as user flow triggers */}
          <button
            style={{ display: 'none' }}
            data-testid="open-submit-request-form-button"
            onClick={this.toggleSubmitRequestModal}
          ></button>
          <button
            style={{ display: 'none' }}
            data-testid="open-product-feedback-form-button"
            onClick={this.toggleProductFeedbackModal}
          ></button>
          <button
            style={{ display: 'none' }}
            data-testid="guided-tours-opt-out-button"
            onClick={this.toggleOptOutOfGuidedTours}
          ></button>

          <Tooltip content={'View user options'} relationship={'label'}>
            <div style={{ margin: '0 10px 0 5px', display: 'inline' }}>
              <ContextButton
                ref={this.userContextRef}
                label=""
                labelIcon={!this.gravatarUrl ? this.getUserIcon() : null}
                labelImage={this.gravatarUrl}
                tabIndex={this.props.tabIndex}
                buttonClass="btnTransparentIcon"
                alignLeft={true}
                maxWidth={175}
                includeCaret={false}
              >
                <SelectableList onSelect={this.handleSelectUserContextMenu} options={accountNavOptions} />
              </ContextButton>
            </div>
          </Tooltip>
        </div>
        {submitRequestModal}
        {productFeedbackModal}
        {aboutModal}
        <ActionCentrePanel
          panelOpen={this.state.notificationPanelOpen}
          setPanelOpen={(po) => {
            this.setState({ notificationPanelOpen: po });
          }}
        />
      </nav>
    );
  }
}

NavBar.defaultProps = {
  onLogoutClick: function (e) {
    console.log('Warning: onLogoutClick not implemented');
  },
};

function mapStateToProps(state) {
  return {
    app: state.app,
    alert: state.data.alert,
  };
}

const ComposedNavBar = compose(connect(mapStateToProps))(NavBar);

export default React.forwardRef(({ ...props }, ref) => {
  const menuOptions = useMenuOptions();

  return <ComposedNavBar {...props} forwardedRef={ref} menuOptions={menuOptions} />;
});
